import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, CheckCircle, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

interface MelissaRecord {
  document_number: string;
  county: string;
  filing_date: string;
  first_name: string;
  last_name: string;
  seller_full_name: string;
  original_street: string;
  original_city: string;
  original_state: string;
  original_zip: string;
  md_address: string;
  md_city: string;
  md_state: string;
  md_zip: string;
  md_results: string;
  property_address: string;
  buyer: string;
  loan_amount: number | null;
  interest_rate: number | null;
  monthly_payment: number | null;
  term_months: number | null;
  balloon_amount: number | null;
  balloon_due_date: string;
  confidence_score: string;
  status: "good" | "review" | "bad";
  statusReason: string;
}

function classifyRecord(mdResults: string): { status: "good" | "review" | "bad"; reason: string } {
  const codes = mdResults.split(",").map(c => c.trim());
  const hasAS01 = codes.includes("AS01");
  const hasAE02 = codes.includes("AE02");
  const hasAE10 = codes.includes("AE10");
  const hasAE01 = codes.includes("AE01");
  const hasAS12 = codes.includes("AS12");

  if (hasAE02 && !hasAS01) return { status: "bad", reason: "Unknown street" };
  if (hasAE10 && !hasAS01) return { status: "bad", reason: "Address not found" };
  if (hasAE01 && !hasAS01) return { status: "bad", reason: "General error" };
  if (hasAS12) return { status: "review", reason: "Address changed" };
  if (hasAS01) return { status: "good", reason: "Verified" };
  return { status: "review", reason: `Review: ${mdResults}` };
}

function parseNum(val: any): number | null {
  if (val == null || val === "") return null;
  const s = String(val).replace(/[$,\s]/g, "");
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

function parseDate(val: any): string | null {
  if (!val) return null;
  if (typeof val === "number") {
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  const s = String(val).trim();
  if (!s) return null;
  // MM/DD/YYYY
  const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m1) return `${m1[3]}-${m1[1].padStart(2, "0")}-${m1[2].padStart(2, "0")}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
}

export function MelissaImport({ onImportComplete }: { onImportComplete?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [records, setRecords] = useState<MelissaRecord[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<{ updated: number; skipped: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback((f: File) => {
    setFile(f);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

        const parsed: MelissaRecord[] = raw.map((row) => {
          const get = (patterns: string[]) => {
            for (const key of Object.keys(row)) {
              const norm = key.toLowerCase().replace(/[^a-z0-9]/g, "");
              for (const p of patterns) {
                if (norm === p || norm.includes(p)) return String(row[key] || "");
              }
            }
            return "";
          };

          const mdResults = get(["mdresults"]);
          const { status, reason } = classifyRecord(mdResults);

          return {
            document_number: get(["documentnumber"]),
            county: get(["county"]),
            filing_date: get(["filingdate"]),
            first_name: get(["first"]),
            last_name: get(["last"]),
            seller_full_name: get(["sellergrantor"]),
            original_street: get(["st"]),
            original_city: get(["city"]),
            original_state: get(["state"]),
            original_zip: get(["zip"]),
            md_address: get(["mdaddress1"]),
            md_city: get(["mdcity"]),
            md_state: get(["mdstate"]),
            md_zip: get(["mdpostalcode"]),
            md_results: mdResults,
            property_address: get(["propertyaddress"]),
            buyer: get(["buyergrantee"]),
            loan_amount: parseNum(get(["loanamount"])),
            interest_rate: parseNum(get(["interestrate"])),
            monthly_payment: parseNum(get(["monthlypayment"])),
            term_months: parseNum(get(["termmonths"])),
            balloon_amount: parseNum(get(["balloonamount"])),
            balloon_due_date: get(["balloonduedate"]),
            confidence_score: get(["confidencescore"]),
            status,
            statusReason: reason,
          };
        });

        setRecords(parsed);
        const good = parsed.filter(r => r.status === "good").length;
        const review = parsed.filter(r => r.status === "review").length;
        const bad = parsed.filter(r => r.status === "bad").length;
        toast.success(`Parsed ${parsed.length} records: ${good} good, ${review} review, ${bad} bad`);
      } catch (err) {
        toast.error("Failed to parse file");
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const badRecords = records.filter(r => r.status === "bad");
  const goodCount = records.filter(r => r.status === "good").length;

  const handleImport = async () => {
    const toImport = records.filter(r => r.status === "good");
    if (!toImport.length) return;

    // Deduplicate by document_number
    const deduped = new Map<string, MelissaRecord>();
    for (const r of toImport) deduped.set(r.document_number, r);
    const uniqueRecords = Array.from(deduped.values());

    setImporting(true);
    setProgress(0);
    let updated = 0;
    const chunkSize = 100;

    try {
      for (let i = 0; i < uniqueRecords.length; i += chunkSize) {
        const chunk = uniqueRecords.slice(i, i + chunkSize);

        const upsertRows = chunk.map(r => ({
          seller_id: r.document_number,
          county: r.county || null,
          first_name: r.first_name || null,
          last_name: r.last_name || null,
          street_address: r.md_address || null,
          city: r.md_city || null,
          state: r.md_state || null,
          zip_code: r.md_zip || null,
          property_address: r.property_address || null,
          consideration: r.loan_amount,
          recorded_date: parseDate(r.filing_date),
          document_type: null as string | null,
          mailing_status: "active",
        }));

        const { data, error } = await supabase
          .from("mailing_records")
          .upsert(upsertRows, { onConflict: "seller_id", ignoreDuplicates: false })
          .select("id");

        if (error) {
          console.error("Upsert error:", error);
          toast.error(`Error at batch ${Math.floor(i / chunkSize) + 1}: ${error.message}`);
          break;
        }

        updated += data?.length || 0;
        setProgress(Math.round(((i + chunkSize) / uniqueRecords.length) * 100));
      }

      setImportResult({ updated, skipped: badRecords.length });
      toast.success(`Import complete: ${updated} records imported with verified addresses`);
      onImportComplete?.();
    } catch (err) {
      console.error(err);
      toast.error("Import failed");
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  const StatusIcon = ({ s }: { s: string }) => {
    if (s === "good") return <ShieldCheck className="h-3.5 w-3.5 text-green-600" />;
    if (s === "review") return <ShieldAlert className="h-3.5 w-3.5 text-yellow-600" />;
    return <ShieldX className="h-3.5 w-3.5 text-destructive" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Import Melissa Data Cleaned File
        </CardTitle>
        <CardDescription>
          Upload your Melissa-cleaned Excel file. Only verified (AS01) addresses will be imported.
          The MD_Address fields replace the original address. Filing Date maps to Recorded Date.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!records.length && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = ".xlsx,.xls,.csv";
              input.onchange = (e: any) => { if (e.target.files[0]) handleFile(e.target.files[0]); };
              input.click();
            }}
          >
            <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium">Drop your Melissa-cleaned Excel file here</p>
            <p className="text-xs text-muted-foreground mt-1">Supports .xlsx, .xls, .csv</p>
          </div>
        )}

        {records.length > 0 && !importResult && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldCheck className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-lg font-bold">{goodCount}</p>
                  <p className="text-xs text-muted-foreground">Verified — will import</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldAlert className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-lg font-bold">{records.filter(r => r.status === "review").length}</p>
                  <p className="text-xs text-muted-foreground">Needs review</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldX className="h-5 w-5 text-destructive" />
                <div>
                  <p className="text-lg font-bold">{badRecords.length}</p>
                  <p className="text-xs text-muted-foreground">Bad — skipping</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{file?.name} — {records.length} records</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setRecords([]); setFile(null); }}>Clear</Button>
                <Button size="sm" onClick={handleImport} disabled={importing || goodCount === 0}>
                  {importing ? "Importing..." : `Import ${goodCount} Verified Records`}
                </Button>
              </div>
            </div>

            {importing && <Progress value={progress} className="h-2" />}

            {/* Full spreadsheet-like table */}
            <div className="border rounded-md overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="w-8 sticky left-0 bg-background z-10">✓</TableHead>
                    <TableHead className="whitespace-nowrap">Doc #</TableHead>
                    <TableHead className="whitespace-nowrap">County</TableHead>
                    <TableHead className="whitespace-nowrap">Filing Date</TableHead>
                    <TableHead className="whitespace-nowrap">First Name</TableHead>
                    <TableHead className="whitespace-nowrap">Last Name</TableHead>
                    <TableHead className="whitespace-nowrap">Original Street</TableHead>
                    <TableHead className="whitespace-nowrap">Original City</TableHead>
                    <TableHead className="whitespace-nowrap">Orig St</TableHead>
                    <TableHead className="whitespace-nowrap">Orig Zip</TableHead>
                    <TableHead className="whitespace-nowrap font-bold">MD Address</TableHead>
                    <TableHead className="whitespace-nowrap font-bold">MD City</TableHead>
                    <TableHead className="whitespace-nowrap font-bold">MD State</TableHead>
                    <TableHead className="whitespace-nowrap font-bold">MD Zip</TableHead>
                    <TableHead className="whitespace-nowrap">Buyer</TableHead>
                    <TableHead className="whitespace-nowrap">Property Address</TableHead>
                    <TableHead className="whitespace-nowrap">Loan Amt</TableHead>
                    <TableHead className="whitespace-nowrap">Rate</TableHead>
                    <TableHead className="whitespace-nowrap">Pmt</TableHead>
                    <TableHead className="whitespace-nowrap">Term</TableHead>
                    <TableHead className="whitespace-nowrap">Balloon</TableHead>
                    <TableHead className="whitespace-nowrap">MD Results</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((r, i) => (
                    <TableRow key={i} className={`text-xs ${r.status === "bad" ? "opacity-40" : ""}`}>
                      <TableCell className="sticky left-0 bg-background z-10"><StatusIcon s={r.status} /></TableCell>
                      <TableCell className="font-mono whitespace-nowrap">{r.document_number}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.county}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.filing_date}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.first_name}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.last_name}</TableCell>
                      <TableCell className="whitespace-nowrap max-w-[180px] truncate">{r.original_street}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.original_city}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.original_state}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.original_zip}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{r.md_address}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{r.md_city}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{r.md_state}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{r.md_zip}</TableCell>
                      <TableCell className="whitespace-nowrap max-w-[150px] truncate">{r.buyer}</TableCell>
                      <TableCell className="whitespace-nowrap max-w-[200px] truncate">{r.property_address}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.loan_amount ? `$${r.loan_amount.toLocaleString()}` : ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.interest_rate ? `${r.interest_rate}%` : ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.monthly_payment ? `$${r.monthly_payment.toLocaleString()}` : ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.term_months || ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.balloon_amount ? `$${r.balloon_amount.toLocaleString()}` : ""}</TableCell>
                      <TableCell className="font-mono whitespace-nowrap max-w-[200px] truncate">{r.md_results}</TableCell>
                      <TableCell>
                        <Badge
                          variant={r.status === "good" ? "default" : r.status === "review" ? "secondary" : "destructive"}
                          className="text-[10px] whitespace-nowrap"
                        >
                          {r.statusReason}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {importResult && (
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg border">
            <CheckCircle className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Melissa Import Complete</p>
              <p className="text-sm text-muted-foreground">
                {importResult.updated} records imported with verified addresses.
                {importResult.skipped > 0 && ` ${importResult.skipped} bad addresses skipped.`}
              </p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => { setRecords([]); setFile(null); setImportResult(null); }}>
              Import Another
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
